import ToolTip from '../../ToolTip/ToolTip';
import Chip from '@mui/material/Chip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ViewAssessmentReport from './ViewAssessmentReport';

export const STUDENT_LEADS_TABLE_HEAD = [
  { field: 'sl_no', headerName: 'Sl No', width: 100 },
  {
    field: 'assessment_report',
    headerName: 'Report',
    width: 160,
    sortable: false,
    filterable: false,
    // An explicit button rather than a row click: the grid's row-click path is a
    // double-click/mobile mechanism, and an employee should not have to guess
    // that a row is clickable to reach the level-wise and question-wise data.
    renderCell: (params) => <ViewAssessmentReport params={params} />,
  },
  { field: 'name', headerName: 'Name', width: 200 },
  {
    field: 'phone',
    headerName: 'Phone Number',
    width: 200,
    renderCell: (params) => {
      return (
        <ToolTip
          content={`${params.row.country_code} - ${params.row.phone}`}
          expand={`${params.row.country_code} - ${params.row.phone}`}
        />
      );
    },
  },
  {
    field: 'email',
    headerName: 'Email',
    width: 250,
    renderCell: (params) => {
      return <ToolTip content={params?.row?.email} expand={params?.row?.email} />;
    },
  },

  {
    field: 'class_name',
    headerName: 'Class',
    width: 200,
    renderCell: (params) => {
      const val = params?.row?.class_name;
      return val ? (
        <ToolTip content={val} expand={val} />
      ) : (
        <Chip label="Not attended" color="info" variant="outlined" />
      );
    },
  },
  // ---- Parent section -------------------------------------------------
  // Asked of every class before the child sees a question paper. This is the
  // column that finds warm half-finished leads: a parent can complete it and
  // then close the tab, and until it existed that person looked exactly like
  // someone who never engaged.
  {
    field: 'parent_assessment_completed',
    headerName: 'Parent Section',
    width: 160,
    renderCell: (params) => {
      const done =
        params?.row?.parent_assessment_completed === 1 ||
        params?.row?.parent_assessment_completed === true;
      return (
        <Chip
          size="small"
          icon={done ? <CheckCircleIcon style={{ color: '#4caf50' }} /> : <ErrorOutlineIcon color="error" />}
          label={done ? 'Completed' : 'Not attended'}
        />
      );
    },
  },
  {
    field: 'parent_assessment_score',
    headerName: 'Parent Score',
    width: 150,
    renderCell: (params) => {
      const val = params?.row?.parent_assessment_score;
      const max = params?.row?.parent_assessment_max;
      // 0 is a real score, so check for null/undefined rather than falsiness.
      return val !== null && val !== undefined && val !== '' ? (
        <ToolTip content={`${Number(val)} / ${Number(max ?? 20)}`} expand={`${Number(val)} / ${Number(max ?? 20)}`} />
      ) : (
        <Chip label="Not attended" color="info" variant="outlined" />
      );
    },
  },
  {
    field: 'assessment_track',
    headerName: 'Paper',
    width: 140,
    renderCell: (params) => {
      const val = params?.row?.assessment_track;
      const meta = {
        kg: { label: 'LKG / UKG', color: 'secondary' },
        foundation: { label: 'Foundation', color: 'primary' },
        senior: { label: 'Class 5+', color: 'default' },
      }[val];
      return meta ? (
        <Chip size="small" label={meta.label} color={meta.color} variant="outlined" />
      ) : (
        <Chip label="—" color="info" variant="outlined" />
      );
    },
  },
  // ---- Student section ------------------------------------------------
  {
    field: 'assessment_subject',
    headerName: 'Assessment Subject',
    width: 250,
    renderCell: (params) => {
      const val = params?.row?.assessment_subject;

      const formattedValue = val ? val.charAt(0).toUpperCase() + val.slice(1) : '';

      return formattedValue ? (
        <ToolTip content={formattedValue} expand={formattedValue} />
      ) : (
        <Chip label="Not attended" color="info" variant="outlined" />
      );
    },
  },
  {
    field: 'student_level',
    headerName: 'Assessment Level',
    width: 200,
    renderCell: (params) => {
      const val = params?.row?.student_level;
      const formattedValue = val ? val.charAt(0).toUpperCase() + val.slice(1) : '';
      return formattedValue ? (
        <ToolTip content={formattedValue} expand={formattedValue} />
      ) : (
        <Chip label="Not attended" color="info" variant="outlined" />
      );
    },
  },
  {
    field: 'mark_scored',
    headerName: 'Assessment Mark',
    width: 200,
    renderCell: (params) => {
      const val = params?.row?.mark_scored;
      const max = params?.row?.student_assessment_max;
      if (val === null || val === undefined || val === '') {
        return <Chip label="Not attended" color="info" variant="outlined" />;
      }
      // Show the denominator whenever we have it. A bare "7" is unreadable
      // across papers — it is out of 10 for LKG/UKG but out of 40 for a
      // four-level foundation run, and the two used to be indistinguishable.
      const text =
        max !== null && max !== undefined && max !== ''
          ? `${Number(val)} / ${Number(max)}`
          : `${Number(val)}`;
      return <ToolTip content={text} expand={text} />;
    },
  },

  { field: 'created_at', headerName: 'Date', width: 200 },
  {
    field: 'is_added_to_lead',
    headerName: 'Is added to lead',
    width: 180,
    renderCell: (params) => {
      const added = params?.row?.is_added_to_lead === 1 || params?.row?.is_added_to_lead === true;
      return (
        <Chip
          icon={added ? <CheckCircleIcon style={{ color: '#4caf50' }} /> : <ErrorOutlineIcon color="error" />}
          label={added ? 'Yes' : 'No'}
        />
      );
    },
  },
  {
    field: 'is_course_enrolled',
    headerName: 'Course enrolled',
    width: 180,
    renderCell: (params) => {
      const enrolled = params?.row?.is_course_enrolled === 1 || params?.row?.is_course_enrolled === true;
      return (
        <Chip
          icon={enrolled ? <CheckCircleIcon style={{ color: '#4caf50' }} /> : <ErrorOutlineIcon color="error" />}
          label={enrolled ? 'Yes' : 'No'}
        />
      );
    },
  },
  {
    field: 'assigned_sro',
    headerName: 'OGA name',
    width: 200,
    renderCell: (params) => {
      const val = params?.row?.assigned_sro;
      return val ? (
        <ToolTip content={val} expand={val} />
      ) : (
        <Chip label="Not assigned" color="info" variant="outlined" />
      );
    },
  },

  {
    field: 'assigned_cro',
    headerName: 'CRO name',
    width: 200,
    renderCell: (params) => {
      const val = params?.row?.assigned_cro;
      return val ? (
        <ToolTip content={val} expand={val} />
      ) : (
        <Chip label="Not assigned" color="info" variant="outlined" />
      );
    },
  },

  {
    field: 'course_name',
    headerName: 'Course name',
    width: 250,
    renderCell: (params) => {
      const val = params?.row?.course_name;
      return val ? (
        <ToolTip content={val} expand={val} />
      ) : (
        <Chip label="Not Enrolled" color="info" variant="outlined" />
      );
    },
  },
  {
    field: 'project_name',
    headerName: 'Project name',
    width: 200,
    renderCell: (params) => {
      const val = params?.row?.project_name;
      return val ? (
        <ToolTip content={val} expand={val} />
      ) : (
        <Chip label="Not Enrolled" color="info" variant="outlined" />
      );
    },
  },
];
